import { useEffect } from 'react'

/**
 * Sayfa başlığı ve meta etiketleri — tek küçük mekanizma, ek paket yok.
 *
 * Sunucuda (ön-render) bileşen çizilirken kayda yazılır ve `index.html`'in
 * <head>'ine basılır: arama motoru ve paylaşım robotları JavaScript
 * çalıştırmadan doğru başlığı görür. Tarayıcıda ise rota değişince
 * `document.head` güncellenir.
 */
export interface HeadKaydi {
  title: string
  meta: Array<{ name?: string; property?: string; content: string }>
}

export interface SayfaBasligi {
  title: string
  description: string
  /** Görsel; varsayılan og.png. Mutlak adres olmalı: paylaşım robotları göreli yolu okumaz. */
  image?: string
}

export const SITE_URL = 'https://akillidernek.com'
const SITE_ADI = 'Akıllı Dernek'

/**
 * Sunucu tarafı kayıt. React durumu ya da bağlamı değil, modül değişkeni:
 * ön-render tek iş parçacığında, sayfa sayfa sırayla koşar; render başlamadan
 * `sunucuKaydiBasla` çağrılır, `useHead` çizim sırasında buraya yazar.
 */
let aktifKayit: HeadKaydi | null = null

export function sunucuKaydiBasla(): HeadKaydi {
  aktifKayit = { title: '', meta: [] }
  return aktifKayit
}

/** Çizim sırasında çağrılır; tarayıcıda (kayıt yokken) hiçbir şey yapmaz. */
function sunucuyaYaz(title: string, meta: HeadKaydi['meta']) {
  if (!aktifKayit) return
  aktifKayit.title = title
  aktifKayit.meta = meta
}

function metaListesi(s: SayfaBasligi, yol: string) {
  const title = s.title === SITE_ADI ? s.title : `${s.title} · ${SITE_ADI}`
  const image = s.image ?? `${SITE_URL}/og.png`
  return {
    title,
    meta: [
      { name: 'description', content: s.description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: s.description },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: `${SITE_URL}${yol}` },
      { property: 'og:image', content: image },
      { property: 'og:locale', content: 'tr_TR' },
      { property: 'og:site_name', content: SITE_ADI },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: s.description },
      { name: 'twitter:image', content: image },
    ],
  }
}

export function useHead(s: SayfaBasligi, yol: string) {
  const { title, meta } = metaListesi(s, yol)
  // Sunucu: etkiler çalışmaz, kayıt çizim sırasında yapılır.
  sunucuyaYaz(title, meta)
  useEffect(() => {
    document.title = title
    for (const m of meta) {
      const secici = m.name ? `meta[name="${m.name}"]` : `meta[property="${m.property}"]`
      let el = document.head.querySelector<HTMLMetaElement>(secici)
      if (!el) {
        el = document.createElement('meta')
        if (m.name) el.name = m.name
        if (m.property) el.setAttribute('property', m.property)
        document.head.appendChild(el)
      }
      el.content = m.content
    }
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = `${SITE_URL}${yol}`
  }, [title, meta, yol])
}

function kacir(s: string) {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

export function headHtml(kayit: HeadKaydi, yol: string): string {
  const satirlar = [`<title>${kacir(kayit.title)}</title>`]
  for (const m of kayit.meta) {
    const anahtar = m.name ? `name="${m.name}"` : `property="${m.property}"`
    satirlar.push(`<meta ${anahtar} content="${kacir(m.content)}" />`)
  }
  satirlar.push(`<link rel="canonical" href="${SITE_URL}${yol}" />`)
  return satirlar.join('\n    ')
}
